// Перенос листинга между страницами: код - абзац DOCX без «не разрывать» (keepLines только у заголовков), Word
// делит его по строкам (не меньше 2 строк внизу и вверху), writer по этим границам делит код на части (абзацы в своей
// рамке) с «Продолжение/Окончание листинга N» над следующей частью. В редакторе кода (CodeMirror 6) распорка -
// блочный виджет перед строкой кода: в нём надпись продолжения (у левого поля, белая полоса под ней закрывает боковые
// линии рамки) и верхняя линия рамки следующей части; у части до разрыва нижней линии нет (как в DOCX и у таблицы),
// боковые линии ниже её последней строки и в промежутке между листами закрывает слой PagesOverlay. Виджет ставится
// через поле состояния, которое добавляется в редактор листинга один раз (StateEffect.appendConfig). Модель документа
// не меняется.
import { StateEffect, StateField, type Extension as CMExtension } from '@codemirror/state';
import { Decoration, EditorView, WidgetType, type DecorationSet } from '@codemirror/view';
import { codeViews } from '../tables_figures_code/code/views';

export interface CodeBreak {
  line: number;
  h: number;
  /** «Продолжение/Окончание листинга N» (нет - распорка без надписи и рамок частей) */
  label?: string;
  /** высота надписи с интервалом после */
  labelH?: number;
  /** рамка следующей части и левый край текста - относительно строк кода (ListingInfo) */
  padTop?: number;
  textDx?: number;
  frameDx?: number;
  frameW?: number;
}

const same = (a: number | undefined, b: number | undefined) => Math.abs((a || 0) - (b || 0)) < 0.03;
/** Виджеты одинаковы (строка - не в счёт: от неё вид виджета не зависит). */
function sameLook(a: CodeBreak, b: CodeBreak): boolean {
  return same(a.h, b.h) && (a.label ?? null) === (b.label ?? null) && same(a.labelH, b.labelH)
    && same(a.padTop, b.padTop) && same(a.textDx, b.textDx) && same(a.frameDx, b.frameDx)
    && same(a.frameW, b.frameW);
}
export function sameBreak(a: CodeBreak, b: CodeBreak): boolean { return a.line === b.line && sameLook(a, b); }

class GapWidget extends WidgetType {
  constructor(readonly b: CodeBreak) { super(); }
  eq(o: GapWidget) { return sameLook(o.b, this.b); }
  get estimatedHeight() { return this.b.h; }
  toDOM() {
    const b = this.b;
    const h = b.h;
    const d = document.createElement('div');
    d.className = 'pg-sp pg-sp-code';
    d.style.height = h + 'px';
    d.setAttribute('data-h', String(h));
    d.setAttribute('aria-hidden', 'true');
    if (b.label !== undefined && b.labelH !== undefined) {
      const pt = b.padTop || 0;
      const fx = b.frameDx || 0;
      const fw = b.frameW || 0;
      // у части до разрыва нижней линии рамки нет (writer/listingsplit.py: рамка открыта снизу, как у таблицы)
      // надпись над следующей частью: стиль подписи листинга, у левого поля; полоса - на ширину рамки с запасом
      const lab = document.createElement('div');
      lab.className = 'pg-cont-label pg-lst-cont';
      const left = Math.min(b.textDx || 0, fx - 3);
      lab.style.top = (h - pt - b.labelH) + 'px';
      lab.style.height = b.labelH + 'px';
      lab.style.left = left + 'px';
      lab.style.width = (Math.max(fx + fw + 3, (b.textDx || 0) + 1) - left) + 'px';
      lab.style.paddingLeft = ((b.textDx || 0) - left) + 'px';
      lab.textContent = b.label;
      d.appendChild(lab);
      // верхняя линия рамки следующей части
      const e = document.createElement('div');
      e.className = 'pg-code-edge pg-code-edge-top';
      e.style.top = (h - pt) + 'px';
      e.style.left = fx + 'px';
      e.style.width = fw + 'px';
      d.appendChild(e);
    }
    return d;
  }
  ignoreEvent() { return true; }
}

const setBreaks = StateEffect.define<CodeBreak[]>();
const field = StateField.define<{ deco: DecorationSet; breaks: CodeBreak[] }>({
  create: () => ({ deco: Decoration.none, breaks: [] }),
  update(v, tr) {
    for (const e of tr.effects) {
      if (e.is(setBreaks)) {
        const doc = tr.state.doc;
        const ranges = e.value.filter((b) => b.line >= 1 && b.line <= doc.lines)
          .map((b) => Decoration.widget({ widget: new GapWidget(b), block: true, side: -1 }).range(doc.line(b.line).from));
        return { deco: Decoration.set(ranges, true), breaks: e.value };
      }
    }
    if (!tr.docChanged) return v;
    // правка кода: распорки сдвигаются вместе с текстом; список распорок - по тем, что остались (замена всего кода
    // снаружи - отмена, вставка, импорт - убирает виджеты; раньше список оставался прежним, applyCodeBreaks считал,
    // что распорки стоят, и разрыв листинга пропадал до следующего изменения разметки)
    const deco = v.deco.map(tr.changes);
    const breaks: CodeBreak[] = [];
    deco.between(0, tr.state.doc.length, (from, _to, d) => {
      const w = d.spec.widget;
      if (w instanceof GapWidget) breaks.push({ ...w.b, line: tr.state.doc.lineAt(from).number });
    });
    return { deco, breaks };
  },
  provide: (f) => EditorView.decorations.from(f, (v) => v.deco),
});
const ext: CMExtension = field;
const installed = new WeakSet<EditorView>();

/** Распорки, стоящие сейчас в листинге id: номер строки -> высота. */
export function currentCodeBreaks(id: string): Map<number, number> {
  const v = codeViews.get(id);
  const out = new Map<number, number>();
  if (!v || !installed.has(v)) return out;
  const st = v.state.field(field, false);
  for (const b of st?.breaks || []) out.set(b.line, b.h);
  return out;
}

/** Поставить распорки во все листинги (пустой список - убрать). */
export function applyCodeBreaks(all: Map<string, CodeBreak[]>) {
  for (const [id, v] of codeViews) {
    const want = all.get(id) || [];
    const cur = installed.has(v) ? v.state.field(field, false)?.breaks || [] : [];
    if (cur.length === want.length && cur.every((b, i) => sameBreak(b, want[i]))) continue;
    try {
      if (!installed.has(v)) { v.dispatch({ effects: StateEffect.appendConfig.of(ext) }); installed.add(v); }
      v.dispatch({ effects: setBreaks.of(want) });
    } catch { /* редактор уничтожен */ }
  }
}

export { codeViews };
